import { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaShieldAlt,
  FaServer, 
  FaCheckCircle,
  FaPhone,
  FaExternalLinkAlt,
  FaMicrochip,
  FaMemory,
  FaHdd,
  FaRocket,
  FaNetworkWired,
  FaCloud,
  FaFilter,
  FaSearch,
  FaTh,
  FaList,
  FaChevronLeft,
  FaChevronRight,
  FaGlobe,
  FaRobot,
  FaCode} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext'; // Import du context
import { Helmet } from 'react-helmet-async';

const WAF = () => {
  const { t } = useLanguage(); // Utilisation du hook de traduction
  
  // Animation hooks
  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setCategoriesRef, categoriesInView] = useScrollAnimation(0.1);
  const [setSolutionsRef, solutionsInView] = useScrollAnimation(0.1);

  // État des filtres et recherche
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid ou list
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  // Données FortiWeb WAF centralisées et traduites via le contexte
  const WAF_DATA = useMemo(() => ({
    categories: [
      {
        id: 'appliances-hardware',
        name: t.waf.categories['appliances-hardware'].name,
        description: t.waf.categories['appliances-hardware'].desc,
        icon: <FaServer />
      },
      {
        id: 'virtual-machines',
        name: t.waf.categories['virtual-machines'].name,
        description: t.waf.categories['virtual-machines'].desc,
        icon: <FaCloud />
      },
      {
        id: 'cloud-services',
        name: t.waf.categories['cloud-services'].name,
        description: t.waf.categories['cloud-services'].desc,
        icon: <FaGlobe />
      },
    ],
    solutions: [
      {
        id: 'fortiweb-100e',
        name: 'FortiWeb 100E',
        category: 'appliances-hardware',
        badge: t.lang === 'fr' ? 'Entrée de gamme' : 'Entry level',
        isNew: false,
        image: '/images/WAF/fortiweb-100e.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr' 
          ? ['Petites applications web', 'Sites de démonstration', 'Environnements de test']
          : ['Small web applications', 'Demo sites', 'Test environments'],
        specs: {
          processor: 'Processeur multicœur',
          memory: 'Throughput 50 Mbps',
          storage: '4x GE RJ45',
          network: 'SSL matériel optimisé'
        },
        features: t.lang === 'fr'
          ? ['Protection OWASP Top 10', 'Accélération SSL matérielle', 'Interface web intuitive', 'Détection d\'anomalies ML', 'Déploiement simple']
          : ['OWASP Top 10 Protection', 'Hardware SSL Acceleration', 'Intuitive Web Interface', 'ML Anomaly Detection', 'Simple Deployment']
      },
      {
        id: 'fortiweb-400e',
        name: 'FortiWeb 400E',
        category: 'appliances-hardware',
        badge: 'Performance',
        isNew: false,
        image: '/images/WAF/fortiweb-400e.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Applications d\'entreprise', 'Sites de commerce électronique', 'Portails web']
          : ['Enterprise applications', 'E-commerce sites', 'Web portals'],
        specs: {
          processor: 'Processeurs multicœurs avancés',
          memory: 'Throughput 250 Mbps',
          storage: '4x GE RJ45, 4x GE SFP',
          network: 'Protection DDoS intégrée'
        },
        features: t.lang === 'fr'
          ? ['Débit WAF jusqu\'à 250 Mbps', 'Protection anti-DDoS avancée', 'Détection de bots intelligente', 'SSL Offloading haute performance', 'Gestion centralisée FortiManager']
          : ['WAF throughput up to 250 Mbps', 'Advanced anti-DDoS protection', 'Intelligent bot detection', 'High-performance SSL Offloading', 'FortiManager centralized management']
      },
      {
        id: 'fortiweb-600e',
        name: 'FortiWeb 600E',
        category: 'appliances-hardware',
        badge: t.lang === 'fr' ? 'Haute Performance' : 'High Performance',
        isNew: false,
        image: '/images/WAF/fortiweb-600e.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Applications critiques', 'E-commerce haute charge', 'Plateformes financières']
          : ['Critical applications', 'High-load E-commerce', 'Financial platforms'],
        specs: {
          processor: 'Processeurs optimisés WAF',
          memory: 'Throughput 750 Mbps',
          storage: '4x GE RJ45 (2 bypass), 4x GE SFP',
          network: 'Fonctions bypass matériel'
        },
        features: t.lang === 'fr'
          ? ['Débit WAF jusqu\'à 750 Mbps', 'Bypass matériel pour haute disponibilité', 'Protection API avancée', 'Analyse comportementale ML', 'Intégration FortiGuard AI']
          : ['WAF throughput up to 750 Mbps', 'Hardware bypass for high availability', 'Advanced API protection', 'ML behavioral analysis', 'FortiGuard AI integration']
      },
      {
        id: 'fortiweb-1000e',
        name: 'FortiWeb 1000E',
        category: 'appliances-hardware',
        badge: 'Enterprise',
        isNew: false,
        image: '/images/WAF/fortiweb-1000e.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Data center entreprise', 'Applications multi-tiers', 'Infrastructures critiques']
          : ['Enterprise data center', 'Multi-tier applications', 'Critical infrastructures'],
        specs: {
          processor: 'Architecture multicœur avancée',
          memory: 'Throughput 1,3 Gbps',
          storage: '2x 10GE SFP+, 2x GE RJ45, 4x GE bypass',
          network: 'Interfaces 10GE haute vitesse'
        },
        features: t.lang === 'fr'
          ? ['Performances WAF jusqu\'à 1,3 Gbps', 'Interfaces 10 Gigabit Ethernet', 'Protection zero-day ML', 'Découverte d\'API automatique', 'Compliance PCI-DSS intégrée']
          : ['WAF performance up to 1.3 Gbps', '10 Gigabit Ethernet interfaces', 'ML zero-day protection', 'Automatic API discovery', 'Built-in PCI-DSS compliance']
      },
      {
        id: 'fortiweb-2000f',
        name: 'FortiWeb 2000F',
        category: 'appliances-hardware',
        badge: t.lang === 'fr' ? 'Haute Charge' : 'High Load',
        isNew: false,
        image: '/images/WAF/fortiweb-2000f.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Applications hyperscale', 'CDN protection', 'Plateformes cloud']
          : ['Hyperscale applications', 'CDN protection', 'Cloud platforms'],
        specs: {
          processor: 'Processeurs haute performance',
          memory: 'Throughput 5 Gbps',
          storage: '4x GE bypass, 4x GE SFP, 4x 10GE SFP+',
          network: 'Architecture haute disponibilité'
        },
        features: t.lang === 'fr'
          ? ['Débit WAF jusqu\'à 5 Gbps', 'Architecture de haute disponibilité', 'Protection DDoS avancée', 'Machine Learning intégré', 'Gestion des certificats SSL automatisée']
          : ['WAF throughput up to 5 Gbps', 'High availability architecture', 'Advanced DDoS protection', 'Integrated Machine Learning', 'Automated SSL certificate management']
      },
      {
        id: 'fortiweb-4000f',
        name: 'FortiWeb 4000F',
        category: 'appliances-hardware',
        badge: 'Ultra Performance',
        isNew: false,
        image: '/images/WAF/fortiweb-4000f.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Data centers hyperscale', 'Fournisseurs de services', 'Infrastructures critiques']
          : ['Hyperscale data centers', 'Service providers', 'Critical infrastructures'],
        specs: {
          processor: 'Processeurs dédiés ultra-performance',
          memory: 'Throughput 70 Gbps',
          storage: '8x GE bypass, 10x 10G SFP+, 2x 40G QSFP',
          network: 'Interfaces 40 Gigabit QSFP'
        },
        features: t.lang === 'fr'
          ? ['Performances WAF jusqu\'à 70 Gbps', 'Interfaces 40 Gigabit QSFP', 'Protection APT avancée', 'Analyse forensique intégrée', 'Scalabilité horizontale']
          : ['WAF performance up to 70 Gbps', '40 Gigabit QSFP interfaces', 'Advanced APT protection', 'Integrated forensic analysis', 'Horizontal scalability']
      },
      {
        id: 'fortiweb-vm',
        name: 'FortiWeb VM',
        category: 'virtual-machines',
        badge: t.lang === 'fr' ? 'Virtualisé' : 'Virtualized',
        isNew: false,
        image: '/images/WAF/fortiweb-vm.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Infrastructures virtualisées', 'Cloud privé', 'Environnements hybrides']
          : ['Virtualized infrastructures', 'Private cloud', 'Hybrid environments'],
        specs: {
          processor: 'CPU virtuels scalables',
          memory: 'RAM configurable',
          storage: 'Stockage virtualisé',
          network: 'Interfaces réseau virtuelles'
        },
        features: t.lang === 'fr'
          ? ['Déploiement sur VMware, Hyper-V, KVM', 'Licensing flexible FortiFlex', 'Même FortiOS que les appliances', 'Auto-scaling automatique', 'Intégration orchestrateurs cloud']
          : ['Deployment on VMware, Hyper-V, KVM', 'FortiFlex flexible licensing', 'Same FortiOS as appliances', 'Automatic auto-scaling', 'Cloud orchestrator integration']
      },
      {
        id: 'fortiweb-cloud-aws',
        name: 'FortiWeb Cloud for AWS',
        category: 'cloud-services',
        badge: 'SaaS',
        isNew: false,
        image: '/images/WAF/fortiweb-cloud-aws.jpeg',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Applications AWS', 'Workloads natifs cloud', 'Migration cloud']
          : ['AWS applications', 'Cloud-native workloads', 'Cloud migration'],
        specs: {
          processor: 'Infrastructure AWS managée',
          memory: 'Scaling automatique',
          storage: 'Stockage cloud AWS',
          network: 'Intégration VPC native'
        },
        features: t.lang === 'fr'
          ? ['WAF as a Service sur AWS', 'Déploiement en quelques clics', 'Scaling automatique élastique', 'Intégration AWS WAF native', 'Billing basé sur l\'usage']
          : ['WAF as a Service on AWS', 'Click-to-deploy', 'Elastic auto-scaling', 'Native AWS WAF integration', 'Usage-based billing']
      },
      {
        id: 'fortiweb-cloud-azure',
        name: 'FortiWeb Cloud for Azure',
        category: 'cloud-services',
        badge: 'SaaS',
        isNew: false,
        image: '/images/WAF/fortiweb-cloud-azure.jpg',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Applications Azure', 'DevOps pipelines', 'Modernisation apps']
          : ['Azure applications', 'DevOps pipelines', 'App modernization'],
        specs: {
          processor: 'Infrastructure Azure managée',
          memory: 'Auto-scaling Azure',
          storage: 'Stockage Blob intégré',
          network: 'Integration Azure Virtual Network'
        },
        features: t.lang === 'fr'
          ? ['WAF nativ pour Microsoft Azure', 'Intégration Azure Sentinel', 'DevSecOps pipeline integration', 'Azure AD authentication', 'Compliance Azure built-in']
          : ['Native WAF for Microsoft Azure', 'Azure Sentinel integration', 'DevSecOps pipeline integration', 'Azure AD authentication', 'Built-in Azure compliance']
      },
      {
        id: 'fortiweb-cloud-gcp',
        name: 'FortiWeb Cloud for GCP',
        category: 'cloud-services',
        badge: 'SaaS',
        isNew: false,
        image: '/images/WAF/fortiweb-cloud-gcp.png',
        imageSize: { width: '100%', height: '100%', objectFit: 'contain' },
        imagePosition: { objectPosition: 'center center' },
        workloads: t.lang === 'fr'
          ? ['Google Cloud apps', 'Kubernetes workloads', 'Microservices']
          : ['Google Cloud apps', 'Kubernetes workloads', 'Microservices'],
        specs: {
          processor: 'Infrastructure GCP managée',
          memory: 'Google Cloud scaling',
          storage: 'Cloud Storage intégré',
          network: 'VPC native integration'
        },
        features: t.lang === 'fr'
          ? ['WAF optimisé pour Google Cloud', 'Intégration GKE native', 'Cloud Security Command Center', 'BigQuery analytics integration', 'Google Cloud Armor compatibility']
          : ['Optimized WAF for Google Cloud', 'Native GKE integration', 'Cloud Security Command Center', 'BigQuery analytics integration', 'Google Cloud Armor compatibility']
      },
    ]
  }), [t]);

  // Animation variants
  const animationVariants = {
    fadeInUp: {
      initial: { opacity: 0, y: 30 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.8 }
    },
    scaleIn: {
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.6 }
    }
  };

  // Filtrage des solutions
  const filteredSolutions = useMemo(() => {
    return WAF_DATA.solutions.filter(solution => {
      const matchCategory = selectedCategory === 'all' || solution.category === selectedCategory;
      const matchSearch = solution.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         solution.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         solution.workloads.some(workload => workload.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchCategory && matchSearch;
    });
  }, [selectedCategory, searchTerm, WAF_DATA]);

  // Pagination
  const paginatedSolutions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSolutions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSolutions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredSolutions.length / itemsPerPage);

  // Handlers
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const scrollToSolutions = () => {
    document.getElementById('solutions-section').scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div>
      
      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.waf.seo.title}</title>
        <meta name="description" content={t.waf.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/waf" />
        
        {/* Balises Open Graph (LinkedIn / Twitter) */}
        <meta property="og:title" content={t.waf.seo.title} />
        <meta property="og:description" content={t.waf.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/WAF/fortiweb-400e.png" />
        <meta property="og:type" content="product" />
      </Helmet>
      {/* ---------------------------------- */}

      {/* Section Hero */}
      <section 
        ref={setHeroRef}
        className="hero-section-sii" 
        style={{ 
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/cybersecurity-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3
          }}
        />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  {...animationVariants.scaleIn}
                  transition={{ delay: 0.2 }}
                  className="mb-4"
                >
                  <span className="badge-sii">
                    <FaShieldAlt className="me-2" />
                    {t.waf.hero.badge}
                  </span>
                </motion.div>

                <motion.h1 
                  className="display-3 fw-bold mb-4 text-white"
                  initial={{ opacity: 0, x: -50 }}
                  animate={heroInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  style={{
                    fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                    lineHeight: '1.1',
                    fontWeight: '700'
                  }}
                  dangerouslySetInnerHTML={{ __html: t.waf.hero.title }}
                />

                <motion.p 
                  className="lead text-light mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                    maxWidth: '600px',
                    lineHeight: '1.6',
                    color: 'rgba(255,255,255,0.9)'
                  }}
                >
                  {t.waf.hero.subtitle}
                </motion.p>

                <motion.div
                  className="d-flex flex-wrap gap-3 mb-5"
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.7 }}
                >
                  <Button 
                    size="lg" 
                    onClick={scrollToSolutions}
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      background: '#d90000',
                      color: 'white',
                      border: 'none',
                      boxShadow: '0 4px 16px rgba(217, 0, 0, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaRocket className="me-2" />
                    {t.waf.hero.btnExplore}
                  </Button>
                  <Button 
                    variant="outline-light" 
                    size="lg"
                    href="tel:+21671940459"
                    style={{
                      borderRadius: '16px',
                      padding: '1rem 2.5rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderWidth: '2px',
                      background: 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <FaPhone className="me-2" />
                    {t.waf.hero.btnContact}
                  </Button>
                </motion.div>
              </motion.div>
            </Col>

            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={heroInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-center"
              >
                <div 
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '24px',
                    padding: '3rem',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <h3 className="text-white mb-4 fw-bold">
                    {t.waf.why.title}
                  </h3>
                  <div className="text-start">
                    {t.waf.why.list.map((feature, index) => (
                      <motion.div
                        key={index}
                        className="d-flex align-items-center mb-3"
                        initial={{ opacity: 0, x: -20 }}
                        animate={heroInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                      >
                        <FaCheckCircle 
                          className="me-3 text-success" 
                          style={{ minWidth: '20px' }}
                        />
                        <span className="text-white">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section Catégories */}
      <section className="categories-section py-5 bg-light">
        <Container fluid>
          <motion.div
            ref={setCategoriesRef}
            variants={animationVariants.fadeInUp}
            initial="initial"
            animate={categoriesInView ? "animate" : "initial"}
            className="text-center mb-5"
          >
            <h2 className="fw-bold mb-3" dangerouslySetInnerHTML={{ __html: t.waf.categories.title }} />
            <p className="text-muted lead">
              {t.waf.categories.subtitle}
            </p>
          </motion.div>

          <Row className="g-4 justify-content-center">
            {WAF_DATA.categories.map((category, index) => (
              <Col key={category.id} lg={3} md={6}>
                <motion.div
                  variants={animationVariants.scaleIn}
                  initial="initial"
                  animate={categoriesInView ? "animate" : "initial"}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className="h-100 text-center border-0 shadow-sm category-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleCategoryChange(category.id)}
                  >
                    <Card.Body className="p-4">
                      <div className="mb-3" style={{ fontSize: '3rem', color: '#d90000' }}>
                        {category.icon}
                      </div>
                      <h5 className="fw-bold mb-2">{category.name}</h5>
                      <p className="text-muted small">{category.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Section Solutions */}
      <section id="solutions-section" className="solutions-section py-5">
        <Container fluid>
          <motion.div
            ref={setSolutionsRef}
            variants={animationVariants.fadeInUp}
            initial="initial"
            animate={solutionsInView ? "animate" : "initial"}
            className="mb-5"
          >
            <Row>
              {/* Sidebar Filtres */}
              <Col lg={3}>
                <div style={{
                  position: 'sticky',
                  top: '100px',
                  background: '#fff',
                  borderRadius: '20px',
                  padding: '2rem',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef',
                  height: 'fit-content'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '2rem',
                    paddingBottom: '1rem',
                    borderBottom: '2px solid #e9ecef'
                  }}>
                    <FaFilter style={{ 
                      fontSize: '1.2rem', 
                      color: '#d90000',
                      marginRight: '0.75rem'
                    }} />
                    <h5 style={{
                      margin: 0,
                      fontWeight: 700,
                      color: '#1a202c',
                      fontSize: '1.1rem'
                    }}>
                      {t.waf.sidebar.title}
                    </h5>
                  </div>

                  {/* Barre de recherche */}
                  <div className="mb-4">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {t.waf.sidebar.searchLabel}
                    </label>
                    <div className="position-relative">
                      <FaSearch 
                        style={{
                          position: 'absolute',
                          left: '1rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#999',
                          fontSize: '0.9rem'
                        }}
                      />
                      <Form.Control
                        type="text"
                        placeholder={t.waf.sidebar.searchPlaceholder}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={{
                          borderRadius: '12px',
                          border: '2px solid #e0e0e0',
                          paddingLeft: '2.5rem',
                          paddingRight: '1rem',
                          paddingTop: '0.75rem',
                          paddingBottom: '0.75rem',
                          fontSize: '0.9rem',
                          background: '#f8f9fa'
                        }}
                      />
                    </div>
                  </div>

                  {/* Filtre par catégorie */}
                  <div className="mb-4">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {t.waf.sidebar.catLabel}
                    </label>
                    
                    <div className="d-grid gap-2">
                      <Button
                        variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'}
                        onClick={() => handleCategoryChange('all')}
                        style={{
                          borderRadius: '12px',
                          padding: '0.75rem 1rem',
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          textAlign: 'left',
                          border: selectedCategory === 'all' ? '2px solid #d90000' : '1px solid #dee2e6'
                        }}
                      >
                        <FaShieldAlt className="me-2" style={{ fontSize: '0.8rem' }} />
                        {t.waf.sidebar.all} ({WAF_DATA.solutions.length})
                      </Button>

                      {WAF_DATA.categories.map(category => (
                        <Button
                          key={category.id}
                          variant={selectedCategory === category.id ? 'danger' : 'outline-secondary'}
                          onClick={() => handleCategoryChange(category.id)}
                          style={{
                            borderRadius: '12px',
                            padding: '0.75rem 1rem',
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            textAlign: 'left',
                            border: selectedCategory === category.id ? '2px solid #d90000' : '1px solid #dee2e6'
                          }}
                        >
                          <span className="me-2" style={{ fontSize: '0.8rem' }}>
                            {category.icon}
                          </span>
                          {category.name}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Résultats */}
                  <div style={{
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
                    borderRadius: '12px',
                    border: '1px solid #dee2e6',
                    marginBottom: '1.5rem'
                  }}>
                    <p style={{ 
                      color: '#495057', 
                      margin: 0,
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      textAlign: 'center'
                    }}>
                      <strong style={{ color: '#d90000' }}>{filteredSolutions.length}</strong> {t.waf.sidebar.found}
                    </p>
                    {totalPages > 1 && (
                      <p style={{ 
                        color: '#6c757d', 
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.75rem',
                        textAlign: 'center'
                      }}>
                        Page {currentPage} {t.waf.sidebar.pageOf} {totalPages}
                      </p>
                    )}
                  </div>

                  {/* Vue mode */}
                  <div className="mb-3">
                    <label style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#4a5568',
                      marginBottom: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block'
                    }}>
                      {t.waf.sidebar.viewLabel}
                    </label>
                    <div className="btn-group d-grid" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                      <Button
                        variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'}
                        onClick={() => setViewMode('grid')}
                        style={{ border: 'none', fontSize: '0.9rem' }}
                      >
                        <FaTh className="me-2" />
                        {t.waf.sidebar.grid}
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'danger' : 'outline-secondary'}
                        onClick={() => setViewMode('list')}
                        style={{ border: 'none', fontSize: '0.9rem' }}
                      >
                        <FaList className="me-2" />
                        {t.waf.sidebar.list}
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Contenu principal */}
              <Col lg={9}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h3 className="fw-bold mb-1">
                      WAF <span style={{ color: '#d90000' }}>FortiWeb</span>
                    </h3>
                    <p className="text-muted mb-0">
                      {filteredSolutions.length} {t.waf.sidebar.found}
                    </p>
                  </div>
                </div>

                {viewMode === 'grid' ? (
                  <Row className="g-4 mb-4">
                    {paginatedSolutions.map((solution, index) => (
                      <Col key={solution.id} lg={6} md={6}>
                        <motion.div
                          variants={animationVariants.scaleIn}
                          initial="initial"
                          animate={solutionsInView ? "animate" : "initial"}
                          transition={{ delay: index * 0.1 }}
                          style={{ height: '100%' }}
                        >
                          <Card className="h-100 shadow-sm border-0 solution-card">
                            <div className="position-relative overflow-hidden" style={{ height: '220px', background: '#f8f9fa' }}>
                              <div className="position-absolute top-0 end-0 p-3 d-flex flex-column gap-2" style={{ zIndex: 2 }}>
                                {solution.isNew && <Badge bg="success"><small>{t.waf.card.new}</small></Badge>}
                                {solution.badge && <Badge bg="danger"><small>{solution.badge}</small></Badge>}
                              </div>
                              {solution.image && (
                                <img 
                                  src={solution.image} 
                                  alt={solution.name}
                                  style={{
                                    width: solution.imageSize?.width || '100%',
                                    height: solution.imageSize?.height || '100%',
                                    objectFit: solution.imageSize?.objectFit || 'contain',
                                    position: 'relative',
                                    zIndex: 1
                                  }}
                                />
                              )}
                            </div>

                            <Card.Body className="d-flex flex-column">
                              <h5 className="fw-bold mb-2">{solution.name}</h5>
                              <div className="d-flex flex-wrap gap-1 mb-3">
                                {solution.workloads.map((w, idx) => (
                                  <Badge key={idx} bg="light" text="dark" className="small">{w}</Badge>
                                ))}
                              </div>

                              <div className="specs-grid mb-3">
                                <Row className="g-2">
                                  <Col xs={6}>
                                    <div className="d-flex align-items-center gap-2">
                                      <FaMicrochip className="text-danger" />
                                      <div>
                                        <small className="text-muted">{t.waf.card.specs.processor}</small>
                                        <div className="small fw-semibold">{solution.specs.processor}</div>
                                      </div>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div className="d-flex align-items-center gap-2">
                                      <FaMemory className="text-danger" />
                                      <div>
                                        <small className="text-muted">{t.waf.card.specs.performance}</small>
                                        <div className="small fw-semibold">{solution.specs.memory}</div>
                                      </div>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div className="d-flex align-items-center gap-2">
                                      <FaHdd className="text-danger" />
                                      <div>
                                        <small className="text-muted">{t.waf.card.specs.connectivity}</small>
                                        <div className="small fw-semibold">{solution.specs.storage}</div>
                                      </div>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div className="d-flex align-items-center gap-2">
                                      <FaNetworkWired className="text-danger" />
                                      <div>
                                        <small className="text-muted">{t.waf.card.specs.network}</small>
                                        <div className="small fw-semibold">{solution.specs.network}</div>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </div>

                              <div className="mb-3 flex-grow-1">
                                <small className="text-muted text-uppercase fw-semibold">{t.waf.card.featuresTitle}</small>
                                <ul className="list-unstyled mt-2">
                                  {solution.features.map((f, idx) => (
                                    <li key={idx} className="d-flex align-items-start gap-2 mb-1">
                                      <FaCheckCircle className="text-success mt-1" size={12} />
                                      <small>{f}</small>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </Card.Body>
                          </Card>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="mb-4">
                    {paginatedSolutions.map((solution, index) => (
                      <motion.div
                        key={solution.id}
                        variants={animationVariants.fadeInUp}
                        initial="initial"
                        animate={solutionsInView ? "animate" : "initial"}
                        transition={{ delay: index * 0.1 }}
                        className="mb-3"
                      >
                        <Card className="border-0 shadow-sm">
                          <Card.Body>
                            <Row className="align-items-center">
                              <Col lg={2} className="text-center">
                                <img src={solution.image} alt={solution.name} style={{ width: '100px', height: '100px', objectFit: 'contain' }} />
                              </Col>
                              <Col lg={7}>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <h5 className="fw-bold mb-0">{solution.name}</h5>
                                  {solution.badge && <Badge bg="danger">{solution.badge}</Badge>}
                                </div>
                                <div className="d-flex flex-wrap gap-1 mb-2">
                                  {solution.workloads.map((w, idx) => <Badge key={idx} bg="light" text="dark" className="small">{w}</Badge>)}
                                </div>
                                <small className="text-muted">{solution.features.join(' • ')}</small>
                              </Col>
                              <Col lg={3} className="border-start">
                                <div className="ps-3">
                                  <div className="small mb-1"><strong>{t.waf.card.specs.performance}:</strong> {solution.specs.memory}</div>
                                  <div className="small"><strong>{t.waf.card.specs.network}:</strong> {solution.specs.network}</div>
                                </div>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <FaChevronLeft />
                    </Button>
                    <div className="d-flex gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          variant={currentPage === page ? "danger" : "outline-secondary"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      <FaChevronRight />
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section-sii" style={{ padding: '6rem 0', background: '#f8fafc' }}>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              borderRadius: '24px',
              padding: '4rem 3rem',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', fontSize: '2.5rem' }}>
              {t.waf.cta.title}
            </h3>
            <p style={{ fontSize: '1.2rem', marginBottom: '2.5rem', opacity: 0.9 }}>
              {t.waf.cta.subtitle}
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Button variant="light" size="lg" style={{ borderRadius: '16px', fontWeight: 600 }}>
                <FaPhone className="me-2" />
                {t.waf.cta.btnExpert}
              </Button>
              <Button variant="outline-light" size="lg" style={{ borderRadius: '16px', fontWeight: 600 }}>
                <FaExternalLinkAlt className="me-2" />
                {t.waf.cta.btnDemo}
              </Button>
            </div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default WAF;